/* NEMO cart: Shopify owns prices; this coordinator owns cart UI mutations. */
(() => {
  if (window.NemoCart) return;
  const roots = () => [...document.querySelectorAll('cart-items-component[data-section-id]')];
  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;
  let tail = Promise.resolve(), pending = 0, stale = false, externalTimer;
  const config = () => {
    const el = document.querySelector('[data-nemo-cart-config]');
    return { gift: el?.dataset.giftEnabled === 'true', giftId: Number(el?.dataset.giftId || 52994099806517), threshold: 700000 };
  };
  const welcome = () => {
    const el = document.querySelector('[data-nemo-welcome-config]');
    return { code: el?.dataset.nemoWelcomeConfig?.trim() || '', firstOrder: el?.dataset.nemoWelcomeFirstOrder === 'true' };
  };
  const session = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch { /* Optional preference only. */ } },
    clear(key) { try { sessionStorage.removeItem(key); } catch { /* Optional preference only. */ } },
  };
  function announce(message, error = false) {
    for (const root of roots()) {
      const node = root.querySelector('[data-nemo-cart-message]');
      if (node) { node.hidden = !message; node.textContent = message; node.dataset.error = String(error); }
      const retry = root.querySelector('[data-nemo-cart-retry]');
      if (retry) retry.hidden = !stale;
    }
  }
  function busy() {
    const waiting = pending > 0;
    for (const root of roots()) {
      root.setAttribute('aria-busy', String(waiting));
      for (const el of root.querySelectorAll('button, input, select')) {
        if (el.matches('[data-nemo-dialog-close], [data-nemo-cart-retry]')) continue;
        const lock = waiting || (stale && el.matches('[name="checkout"]'));
        if (lock && !el.disabled) { el.dataset.nemoDisabled = 'true'; el.disabled = true; }
        else if (!lock && el.dataset.nemoDisabled) { el.disabled = false; delete el.dataset.nemoDisabled; }
      }
    }
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(route(path), {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || data.errors) {
        const description = data.description || data.message || data.errors;
        throw new Error(typeof description === 'string' ? description : 'Shopify could not update this cart.');
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The connection is slow. Refresh to check your cart before trying again.');
      throw error;
    } finally { clearTimeout(timeout); }
  }
  const read = () => request('cart.js');
  const write = (path, body) => request(path, body); // Never replay a timed-out POST.
  const welcomeKey = cart => `nemo-welcome-choice:${cart.token}`;
  async function maybeAutoWelcome(cart) {
    const { code, firstOrder } = welcome();
    if (!code || !firstOrder || !cart.item_count || cart.currency !== 'INR' || session.get(welcomeKey(cart))) return cart;
    if ((cart.discount_codes || []).some(item => item.code?.toLowerCase() !== code.toLowerCase())) return cart;
    if ((cart.discount_codes || []).some(item => item.code?.toLowerCase() === code.toLowerCase())) return cart;
    session.set(welcomeKey(cart), 'attempted');
    try {
      const updated = await write('cart/update.js', { discount: code });
      if (updated.discount_codes?.some(item => item.code?.toLowerCase() === code.toLowerCase() && item.applicable)) return updated;
      await write('cart/update.js', { discount: '' });
      return read();
    } catch {
      return read();
    }
  }
  const identity = (item) => JSON.stringify([
    Number(item.variant_id ?? item.variant), Number(item.selling_plan_allocation?.selling_plan?.id ?? item.plan ?? 0),
    Object.entries(item.properties || {}).filter(([, value]) => value != null && value !== '').sort(([a], [b]) => a.localeCompare(b)),
  ]);
  function resolveLine(cart, key, storedIdentity) {
    const exact = cart.items.find(item => item.key === key);
    if (exact) return exact;
    const candidates = cart.items.filter(item => identity(item) === identity(storedIdentity));
    if (candidates.length !== 1) throw new Error('Your cart changed. Please refresh and choose the item again.');
    return candidates[0];
  }
  async function reconcileGift(cart, explicit = false) {
    const cfg = config();
    if (!cfg.gift) return cart;
    const key = `nemo-gift-dismissed:${cart.token}`;
    let gifts = cart.items.filter(item => item.variant_id === cfg.giftId);
    const qualifying = cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0);
    const eligible = cart.currency === 'INR' && qualifying >= cfg.threshold;
    if (!eligible) session.clear(key);
    if (explicit) session.clear(key);
    // Remove all ineligible gift lines, duplicate lines and any unexpected priced gift.
    let retained = false;
    for (const gift of gifts) {
      const keep = eligible && !retained && gift.final_line_price === 0;
      if (!keep) await write('cart/change.js', { id: gift.key, quantity: 0 });
      else if (gift.quantity !== 1) await write('cart/change.js', { id: gift.key, quantity: 1 });
      if (keep) retained = true;
    }
    if (eligible && !retained && !session.get(key)) {
      // Mark attempt before writing: an ambiguous timeout must never auto-add twice.
      session.set(key, 'attempted');
      await write('cart/add.js', { items: [{ id: cfg.giftId, quantity: 1, properties: { _nemo_reward: 'net-7000' } }] });
    }
    return read();
  }
  function updateCounts(cart) {
    document.querySelectorAll('[ref="cartBubbleCount"]').forEach(el => {
      el.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
      el.classList.toggle('hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[ref="cartBubble"]').forEach(el => el.classList.toggle('visually-hidden', cart.item_count === 0));
    document.querySelectorAll('[ref="cartBubbleText"] .visually-hidden').forEach(el => {
      el.textContent = `${cart.item_count} items in cart`;
    });
    document.querySelectorAll('quantity-selector-component[data-variant-id]').forEach(el => {
      const count = cart.items.filter(item => String(item.variant_id) === el.dataset.variantId).reduce((sum, item) => sum + item.quantity, 0);
      el.querySelector('[data-cart-quantity]')?.setAttribute('data-cart-quantity', String(count));
      el.updateCartQuantity?.();
    });
  }
  async function render(cart) {
    const currentRoots = roots();
    const ids = [...new Set(currentRoots.map(el => el.dataset.sectionId))];
    const sections = {};
    // Section Rendering API accepts at most five section IDs per request.
    for (let i = 0; i < ids.length; i += 5) {
      const url = new URL(location.href);
      url.searchParams.set('sections', ids.slice(i, i + 5).join(','));
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Cart display could not refresh.');
        Object.assign(sections, await response.json());
      } finally { clearTimeout(timer); }
    }
    // Validate every replacement before touching the visible cart.
    const ordinals = new Map();
    const replacements = currentRoots.map(root => {
      const id = root.dataset.sectionId;
      if (typeof sections[id] !== 'string') throw new Error('Cart display could not refresh.');
      const doc = new DOMParser().parseFromString(sections[id], 'text/html');
      const matches = [...doc.querySelectorAll('cart-items-component')].filter(el => el.dataset.sectionId === id && el.hasAttribute('data-drawer') === root.hasAttribute('data-drawer'));
      const group = `${id}:${root.hasAttribute('data-drawer')}`;
      const index = ordinals.get(group) || 0;
      ordinals.set(group, index + 1);
      const replacement = matches[index];
      if (!replacement) throw new Error('Cart display could not refresh.');
      return { root, replacement, doc, id };
    });
    for (const { root, replacement, doc, id } of replacements) {
      const active = root.contains(document.activeElement) ? document.activeElement : null;
      const activeRow = active?.closest('[data-nemo-identity]');
      const focusIdentity = activeRow?.dataset.nemoIdentity;
      const activeName = active?.getAttribute('name');
      const activeDialog = root.querySelector('dialog[open][data-nemo-dialog]')?.dataset.nemoDialog;
      const openDetails = [...root.querySelectorAll('details[open][data-nemo-details]')].map(el => el.dataset.nemoDetails);
      const oldProgress = root.querySelector('.nemo-rewards-v10__fill')?.style.width;
      const scrollHost = root.closest('dialog')?.querySelector('.cart-drawer__items');
      const scroll = scrollHost?.scrollTop;
      // Keep the actual drawer dialog and its browser-history state intact.
      root.replaceChildren(...replacement.childNodes);
      root.classList.toggle('cart-page--empty', replacement.classList.contains('cart-page--empty'));
      const drawer = root.closest('.cart-drawer__dialog');
      const drawerInner = root.closest('.cart-drawer__inner');
      if (drawerInner) {
        drawerInner.classList.toggle('cart-drawer--empty', cart.item_count === 0);
        const nextHeader = replacement.closest('.cart-drawer__inner')?.querySelector('.theme-drawer__header');
        const currentHeader = drawerInner.querySelector('.theme-drawer__header');
        if (nextHeader && currentHeader) currentHeader.replaceWith(nextHeader.cloneNode(true));
      }
      root.updatedCallback?.();
      const liveSection = document.getElementById(`shopify-section-${id}`);
      const responseStyles = doc.querySelector('style[data-section-stylesheet]');
      if (liveSection && responseStyles) {
        const liveStyles = liveSection.querySelector('style[data-section-stylesheet]');
        if (liveStyles) liveStyles.textContent = responseStyles.textContent;
        else liveSection.prepend(responseStyles.cloneNode(true));
      }
      if (drawer) {
        drawer.classList.toggle('cart-drawer--empty', cart.item_count === 0);
        drawer.setAttribute('aria-labelledby', 'cart-drawer-heading');
      }
      for (const detail of root.querySelectorAll('details[data-nemo-details]')) detail.open = openDetails.includes(detail.dataset.nemoDetails);
      if (activeDialog) root.querySelector(`[data-nemo-dialog="${activeDialog}"]`)?.showModal();
      if (scrollHost) scrollHost.scrollTop = scroll;
      const newRow = focusIdentity && [...root.querySelectorAll('[data-nemo-identity]')].find(el => el.dataset.nemoIdentity === focusIdentity);
      if (newRow && activeName) newRow.querySelector(`[name="${CSS.escape(activeName)}"]`)?.focus({ preventScroll: true });
      else if (active && !activeDialog) root.querySelector('[data-nemo-offers-open], a, button')?.focus({ preventScroll: true });
      const fill = root.querySelector('.nemo-rewards-v10__fill');
      if (fill && oldProgress && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const target = fill.style.width;
        fill.animate([{ width: oldProgress }, { width: target }], { duration: 240, easing: 'ease-out' });
      }
    }
    updateCounts(cart);
    stale = false;
    document.dispatchEvent(new CustomEvent('nemo:cart-updated', { detail: { cart } }));
    document.querySelectorAll('cart-items-component').forEach(root => root.dispatchEvent(new CustomEvent('cart-section:restored', { bubbles: true })));
    const headerActions = document.querySelector('header-actions');
    const liveRegion = headerActions?.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = `${cart.item_count} items in cart`;
    busy();
  }
  function enqueue(operation, message = '') {
    const active = document.activeElement;
    const owner = active?.closest('cart-items-component');
    const focus = owner ? {
      section: owner.dataset.sectionId,
      row: active.closest('[data-nemo-identity]')?.dataset.nemoIdentity,
      name: active.getAttribute('name'),
      remove: active.matches('.cart-items__remove'),
      dialog: active.closest('[data-nemo-dialog]')?.dataset.nemoDialog,
    } : null;
    pending++;
    busy();
    const task = tail.then(async () => {
      announce('Updating your cart…');
      let warning = '';
      try {
        let cart = await read();
        if (operation) cart = await operation(cart);
        try { cart = await reconcileGift(cart); } catch (error) { warning = `Gift update: ${error.message}`; cart = await read(); }
        cart = await maybeAutoWelcome(cart);
        await render(cart);
        announce(warning || message, Boolean(warning));
      } catch (error) {
        // A failed response does not prove the write failed. Re-read only; never re-add.
        stale = true;
        try { await render(await read()); } catch { /* Keep old content visible and checkout locked. */ }
        announce(`${error.message}${stale ? ' Refresh your cart to continue.' : ''}`, true);
      } finally {
        pending--; busy();
        if (focus && pending === 0) {
          const owner = roots().find(root => root.dataset.sectionId === focus.section);
          const row = [...(owner?.querySelectorAll('[data-nemo-identity]') || [])].find(el => el.dataset.nemoIdentity === focus.row);
          const target = row?.querySelector(focus.remove ? '.cart-items__remove' : `[name="${CSS.escape(focus.name || '')}"]`)
            || (focus.dialog ? owner?.querySelector(`[data-nemo-dialog="${focus.dialog}"] button`) : owner?.querySelector('[data-nemo-offers-open]'));
          target?.focus({ preventScroll: true });
        }
      }
    });
    tail = task.catch(() => {});
    return task;
  }
  window.NemoCart = {
    refresh: () => enqueue(),
    change(row, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0 || pending || stale) return;
      const key = row.dataset.key, storedIdentity = JSON.parse(row.dataset.nemoIdentity);
      return enqueue(async cart => {
        const item = resolveLine(cart, key, storedIdentity);
        if (item.variant_id === config().giftId && quantity === 0) session.set(`nemo-gift-dismissed:${cart.token}`, 'removed');
        return write('cart/change.js', { id: item.key, quantity });
      }, 'Cart updated.');
    },
    discount(code, remove = false, group = '') {
      code = code.trim();
      if (!code || pending || stale) return;
      return enqueue(async cart => {
        const isWelcome = code.toLowerCase() === welcome().code.toLowerCase();
        const preferenceKey = welcomeKey(cart);
        if (isWelcome) session.set(preferenceKey, remove ? 'off' : 'on');
        else if (!remove) session.set(preferenceKey, 'other-code');
        const codes = (cart.discount_codes || []).map(item => item.code).filter(Boolean);
        const welcomeCode = welcome().code.toLowerCase();
        const productCodes = ['triple vitamin shower', 'rust300', 'acf300'];
        const other = codes.filter(value => {
          const key = value.toLowerCase();
          if (key === code.toLowerCase()) return false;
          if (remove) return true;
          if ((isWelcome || code.toLowerCase() === 'diwali21') && productCodes.includes(key)) return false;
          if ((group === 'product' || productCodes.includes(code.toLowerCase())) && (key === welcomeCode || key === 'diwali21')) return false;
          return true;
        });
        const updated = await write('cart/update.js', { discount: (remove ? other : [...other, code]).join(',') });
        if (!remove && !updated.discount_codes?.some(item => item.code.toLowerCase() === code.toLowerCase() && item.applicable)) {
          await write('cart/update.js', { discount: codes.join(',') });
          throw new Error('This code could not be applied. Check the required products, first-order eligibility and other codes.');
        }
        return updated;
      }, remove ? 'Code removed.' : 'Code accepted. Your current savings are shown below.');
    },
  };
  document.addEventListener('submit', event => {
    if (event.target.matches('[data-nemo-discount-form]')) {
      event.preventDefault();
      window.NemoCart.discount(new FormData(event.target).get('discount')?.toString() || '');
    } else if (event.target.matches('.cart-form') && (pending || stale)) {
      event.preventDefault();
      announce('Please wait for your cart to finish updating.', true);
    }
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('button, a');
    if (!button) return;
    if (button.matches('[name="checkout"]') && (pending || stale)) { event.preventDefault(); return; }
    const root = button.closest('cart-items-component');
    if (button.matches('[data-nemo-cart-retry]')) window.NemoCart.refresh();
    if (button.matches('[data-nemo-offers-open]')) root?.querySelector('[data-nemo-dialog="offers"]')?.showModal();
    if (button.matches('[data-nemo-terms-open]')) root?.querySelector('[data-nemo-dialog="rewards"]')?.showModal();
    if (button.matches('[data-nemo-dialog-close]')) {
      const dialog = button.closest('dialog');
      dialog?.close();
      root?.querySelector(dialog?.dataset.nemoDialog === 'rewards' ? '[data-nemo-terms-open]' : '[data-nemo-offers-open]')?.focus();
    }
    if (button.matches('[data-nemo-copy-code]')) {
      try { await navigator.clipboard.writeText(button.dataset.nemoCopyCode); button.textContent = 'Copied'; }
      catch { announce(`Code: ${button.dataset.nemoCopyCode}. Select the code in the input to copy it.`); }
    }
    if (button.matches('[data-nemo-remove-code]')) window.NemoCart.discount(button.dataset.nemoRemoveCode, true);
    if (button.matches('[data-nemo-offer-action]')) window.NemoCart.discount(button.dataset.nemoCode, button.dataset.nemoOfferAction === 'remove', button.dataset.nemoGroup || '');
    if (button.matches('[data-nemo-welcome-action]')) window.NemoCart.discount(button.dataset.nemoWelcomeCode, button.dataset.nemoWelcomeAction === 'remove');
    if (button.matches('[data-nemo-claim-gift]') && !pending && !stale) enqueue(cart => reconcileGift(cart, true), 'Gift checked.');
    if (button.matches('[data-nemo-cart-quick-add]') && !pending && !stale) {
      enqueue(async () => {
        await write('cart/add.js', { items: [{ id: Number(button.dataset.nemoCartQuickAdd), quantity: 1 }] });
        return read();
      }, 'Added to your cart.');
    }
  });
  const externalUpdate = async event => {
    if (event.promise) {
      try { const result = await event.promise; if (result?.detail?.didError) return; } catch { return; }
    }
    if (event.detail?.data?.didError) return;
    clearTimeout(externalTimer);
    externalTimer = setTimeout(() => window.NemoCart.refresh(), 60);
  };
  document.addEventListener('shopify:cart:lines:update', externalUpdate);
  document.addEventListener('shopify:cart:discount:update', externalUpdate);
  document.addEventListener('cart:update', externalUpdate);
  document.addEventListener('discount:update', externalUpdate);
  document.addEventListener('shopify:section:load', externalUpdate);
  window.addEventListener('pageshow', event => { if (event.persisted) window.NemoCart.refresh(); });
  // Existing server-rendered content stays visible during the initial gift/cart check.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.NemoCart.refresh(), { once: true });
  else window.NemoCart.refresh();
})();
